import { useState, useMemo, useEffect, type FormEvent } from 'react';
import { getAdminEvents, createAdminEvent, updateAdminEvent, deleteAdminEvent, batchUpdateEventStatus, type FrontendEvent } from '@/services/admin/events.admin.api';
import type { EventType, EventStatus } from '@/mocks/events';
import ImageUploader from '@/components/base/ImageUploader';
import GalleryUploader from '@/components/base/GalleryUploader';
import PricingSettings from './components/PricingSettings';
import { useRBAC } from '@/hooks/useRBAC';
import NoPermission from '@/components/base/NoPermission';
import { getEventAgendas, createAgenda, updateAgenda, deleteAgenda, type Agenda } from '@/services/admin/agenda.admin.api';
import { getEventGuests, createEventGuest, updateEventGuest, deleteEventGuest, type EventGuest } from '@/services/admin/event-guest.admin.api';
import { getAdminGuests } from '@/services/admin/guests.admin.api';

const REGION_OPTIONS = [
  { value: 'asia', label: '亚洲' },
  { value: 'europe', label: '欧洲' },
  { value: 'americas', label: '美洲' },
  { value: 'africa', label: '非洲/中东' },
  { value: 'oceania', label: '大洋洲' },
];

const TYPE_LABEL: Record<EventType, string> = {
  offline: '线下',
  online: '线上',
  hybrid: '混合',
};

const STATUS_LABEL: Record<EventStatus, string> = {
  upcoming: '即将举行',
  past: '已结束',
};

const STATUS_STYLE: Record<EventStatus, string> = {
  upcoming: 'bg-emerald-100 text-emerald-700',
  past: 'bg-gray-100 text-gray-500',
};

const TYPE_STYLE: Record<EventType, string> = {
  offline: 'bg-blue-50 text-blue-700',
  online: 'bg-sky-50 text-sky-600',
  hybrid: 'bg-amber-50 text-amber-700',
};

const BLANK: Partial<FrontendEvent> = {
  title: '', titleEn: '',
  date: '', endDate: '',
  location: '', locationEn: '',
  country: '',
  region: 'asia',
  type: 'offline',
  status: 'upcoming',
  cover: '',
  description: '', descriptionEn: '',
  tags: [], tagsEn: [],
  month: 1,
  maxCapacity: undefined,
  registrationStart: '',
  registrationDeadline: '',
  gallery: [],
  // 价格设置
  isPaidEvent: false,
  currency: 'USD',
  earlyBirdEnabled: false,
  earlyBirdPrice: 0,
  earlyBirdStartDate: '',
  earlyBirdEndDate: '',
  regularPrice: 0,
  priceEndDate: '',
  tokenGatingEnabled: false,
  tokenSymbol: 'GTRA',
  tokenContractAddress: '',
  tokenMinHolding: 0,
  tokenHoldersOnly: false,
};

export default function AdminEventsPage() {
  const { can } = useRBAC();
  const canView = can('events:view');
  const canWrite = can('events:write');
  const canDelete = can('events:delete');

  const [events, setEvents] = useState<FrontendEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<FrontendEvent | null>(null);
  const [form, setForm] = useState<Partial<FrontendEvent>>(BLANK);
  const [tagsInput, setTagsInput] = useState('');
  const [tagsEnInput, setTagsEnInput] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [batchDeleteConfirm, setBatchDeleteConfirm] = useState(false);
  const [toast, setToast] = useState('');
  const [isCopy, setIsCopy] = useState(false);
  const [saving, setSaving] = useState(false);
  // Batch selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  
  // Tab state for modal
  const [activeTab, setActiveTab] = useState<'basic' | 'gallery' | 'agenda' | 'guests' | 'pricing'>('basic');
  
  // Gallery state
  const [galleryArr, setGalleryArr] = useState<string[]>([]);
  
  // Agenda state
  const [agendas, setAgendas] = useState<Agenda[]>([]);
  const [agendaLoading, setAgendaLoading] = useState(false);
  const [showAgendaModal, setShowAgendaModal] = useState(false);
  const [editingAgenda, setEditingAgenda] = useState<Agenda | null>(null);
  const [agendaForm, setAgendaForm] = useState<Partial<Agenda>>({
    event_id: 0,
    time: '',
    end_time: '',
    title: '',
    title_en: '',
    description: '',
    description_en: '',
    location: '',
    location_en: '',
    speaker_name: '',
    speaker_title: '',
    speaker_institution: '',
    sort_order: 0,
  });
  
  // Guests state
  const [guests, setGuests] = useState<EventGuest[]>([]);
  const [allGuests, setAllGuests] = useState<any[]>([]);
  const [guestLoading, setGuestLoading] = useState(false);
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [editingGuest, setEditingGuest] = useState<EventGuest | null>(null);
  const [guestForm, setGuestForm] = useState<Partial<EventGuest>>({
    event_id: 0,
    guest_id: 0,
    role: '',
    role_en: '',
    topic: '',
    topic_en: '',
    sort_order: 0,
  });

  // Load events from API
  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      try {
        const data = await getAdminEvents();
        setEvents(data);
      } catch (error) {
        console.error('Failed to load events:', error);
        showToast('加载活动失败');
      } finally {
        setLoading(false);
      }
    };
    if (canView) {
      fetchEvents();
    }
  }, [canView]);

  const filtered = useMemo(() => {
    return events.filter((e) => {
      if (filterStatus !== 'all' && e.status !== filterStatus) return false;
      if (filterType !== 'all' && e.type !== filterType) return false;
      if (search && !e.title.includes(search) && !e.titleEn.toLowerCase().includes(search.toLowerCase())
        && !e.location.includes(search)) return false;
      return true;
    });
  }, [events, search, filterStatus, filterType]);

  // Batch selection helpers
  const allFilteredSelected = filtered.length > 0 && filtered.every((e) => selectedIds.has(e.id));
  const someSelected = selectedIds.size > 0;

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map((e) => e.id)));
    }
  };

  const toggleSelectOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBatchStatus = async (status: EventStatus) => {
    try {
      await batchUpdateEventStatus(Array.from(selectedIds), status);
      setEvents(prev => prev.map(e => selectedIds.has(e.id) ? { ...e, status } : e));
      showToast(`已将 ${selectedIds.size} 场活动设为「${STATUS_LABEL[status]}」`);
      setSelectedIds(new Set());
    } catch (error) {
      console.error('Failed to batch update status:', error);
      showToast('批量更新状态失败');
    }
  };

  const handleBatchDelete = async () => {
    try {
      for (const id of selectedIds) {
        await deleteAdminEvent(id);
      }
      setEvents(prev => prev.filter(e => !selectedIds.has(e.id)));
      showToast(`已删除 ${selectedIds.size} 场活动`);
      setSelectedIds(new Set());
      setBatchDeleteConfirm(false);
    } catch (error) {
      console.error('Failed to batch delete:', error);
      showToast('批量删除失败');
    }
  };

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  // ==================== Agenda Management ====================
  const loadAgendas = async (eventId: number) => {
    setAgendaLoading(true);
    try {
      const data = await getEventAgendas(eventId);
      setAgendas(data);
    } catch (error) {
      console.error('Failed to load agendas:', error);
      showToast('加载议程失败');
    } finally {
      setAgendaLoading(false);
    }
  };

  const openAddAgenda = (eventId: number) => {
    setEditingAgenda(null);
    setAgendaForm({
      event_id: eventId,
      time: '',
      end_time: '',
      title: '',
      title_en: '',
      description: '',
      description_en: '',
      location: '',
      location_en: '',
      speaker_name: '',
      speaker_title: '',
      speaker_institution: '',
      sort_order: agendas.length,
    });
    setShowAgendaModal(true);
  };

  const openEditAgenda = (agenda: Agenda) => {
    setEditingAgenda(agenda);
    setAgendaForm({ ...agenda });
    setShowAgendaModal(true);
  };

  const handleSaveAgenda = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingAgenda?.id) {
        const updated = await updateAgenda(editingAgenda.id, agendaForm);
        if (updated) {
          setAgendas(prev => prev.map(a => a.id === editingAgenda.id ? updated : a));
          showToast('议程已更新');
        } else {
          showToast('更新议程失败');
          setSaving(false);
          return;
        }
      } else {
        const created = await createAgenda(agendaForm);
        if (created) {
          setAgendas(prev => [...prev, created]);
          showToast('议程已添加');
        } else {
          showToast('添加议程失败');
          setSaving(false);
          return;
        }
      }
      setShowAgendaModal(false);
    } catch (error) {
      console.error('Failed to save agenda:', error);
      showToast('保存议程失败');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAgenda = async (id: number) => {
    try {
      await deleteAgenda(id);
      setAgendas(prev => prev.filter(a => a.id !== id));
      showToast('议程已删除');
    } catch (error) {
      console.error('Failed to delete agenda:', error);
      showToast('删除议程失败');
    }
  };

  // ==================== Guest Management ====================
  const loadGuests = async (eventId: number) => {
    setGuestLoading(true);
    try {
      const [eventGuests, allGuestsData] = await Promise.all([
        getEventGuests(eventId),
        getAdminGuests(),
      ]);
      setGuests(eventGuests);
      setAllGuests(allGuestsData);
    } catch (error) {
      console.error('Failed to load guests:', error);
      showToast('加载嘉宾失败');
    } finally {
      setGuestLoading(false);
    }
  };

  const openAddGuest = (eventId: number) => {
    setEditingGuest(null);
    setGuestForm({
      event_id: eventId,
      guest_id: 0,
      role: '',
      role_en: '',
      topic: '',
      topic_en: '',
      sort_order: guests.length,
    });
    setShowGuestModal(true);
  };

  const openEditGuest = (guest: EventGuest) => {
    setEditingGuest(guest);
    setGuestForm({ ...guest });
    setShowGuestModal(true);
  };

  const handleSaveGuest = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingGuest?.id) {
        const updated = await updateEventGuest(editingGuest.id, guestForm);
        if (updated) {
          setGuests(prev => prev.map(g => g.id === editingGuest.id ? updated : g));
          showToast('嘉宾信息已更新');
        } else {
          showToast('更新嘉宾失败');
          setSaving(false);
          return;
        }
      } else {
        const created = await createEventGuest(guestForm);
        if (created) {
          setGuests(prev => [...prev, created]);
          showToast('嘉宾已添加');
        } else {
          showToast('添加嘉宾失败');
          setSaving(false);
          return;
        }
      }
      setShowGuestModal(false);
    } catch (error) {
      console.error('Failed to save guest:', error);
      showToast('保存嘉宾失败');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGuest = async (id: number) => {
    try {
      await deleteEventGuest(id);
      setGuests(prev => prev.filter(g => g.id !== id));
      showToast('嘉宾已移除');
    } catch (error) {
      console.error('Failed to delete guest:', error);
      showToast('移除嘉宾失败');
    }
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ ...BLANK });
    setTagsInput('');
    setTagsEnInput('');
    setIsCopy(false);
    setActiveTab('basic');
    setGalleryArr([]);
    setAgendas([]);
    setGuests([]);
    setShowModal(true);
  };

  const openEdit = (ev: FrontendEvent, asCopy = false) => {
    setEditing(ev);
    setForm({ ...ev });
    setTagsInput((ev.tags || []).join('、'));
    setTagsEnInput((ev.tagsEn || []).join(', '));
    setIsCopy(asCopy);
    setActiveTab('basic');
    setGalleryArr(ev.gallery || []);
    setAgendas([]);
    setGuests([]);
    setShowModal(true);
    // Load agendas and guests when opening edit modal
    if (ev.id) {
      const eventId = parseInt(ev.id);
      loadAgendas(eventId);
      loadGuests(eventId);
    }
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    
    // 检测 cover 是否为 base64 数据
    if (form.cover && form.cover.startsWith('data:image')) {
      showToast('封面图片不支持 base64 格式，请使用上传功能或图片 URL');
      setSaving(false);
      return;
    }
    
    // 验证 tags 长度（防止超过数据库 TEXT 字段限制）
    const tagsArr = tagsInput.split(/[,，、]/).map((s) => s.trim()).filter(Boolean);
    const tagsEnArr = tagsEnInput.split(/[,，、]/).map((s) => s.trim()).filter(Boolean);
    
    const tagsStr = tagsArr.join(',');
    const tagsEnStr = tagsEnArr.join(',');
    
    if (tagsStr.length > 10000) {
      showToast('中文标签过长，请精简或减少标签数量');
      setSaving(false);
      return;
    }
    
    if (tagsEnStr.length > 10000) {
      showToast('英文标签过长，请精简或减少标签数量');
      setSaving(false);
      return;
    }
    
    const dateStr = form.date || '';
    const monthNum = dateStr ? new Date(dateStr).getMonth() + 1 : 1;

    const eventData: Partial<FrontendEvent> = {
      title: form.title || '',
      titleEn: form.titleEn || '',
      date: form.date || '',
      endDate: form.endDate || form.date || '',
      location: form.location || '',
      locationEn: form.locationEn || '',
      country: form.country || '',
      region: (form.region || 'asia') as FrontendEvent['region'],
      type: (form.type || 'offline') as EventType,
      status: (form.status || 'upcoming') as EventStatus,
      cover: form.cover || '/images/events/event-gtf-2026.png',
      description: form.description || '',
      descriptionEn: form.descriptionEn || '',
      tags: tagsArr,
      tagsEn: tagsEnArr,
      month: monthNum,
      maxCapacity: form.maxCapacity ? Number(form.maxCapacity) : undefined,
      registrationStart: (form.registrationStart as string) || '',
      registrationDeadline: (form.registrationDeadline as string) || '',
      gallery: galleryArr,
      // 价格设置 - 确保保存时包含价格字段
      isPaidEvent: form.isPaidEvent ?? false,
      currency: form.currency || 'USD',
      earlyBirdEnabled: form.earlyBirdEnabled ?? false,
      earlyBirdPrice: form.earlyBirdPrice ?? 0,
      earlyBirdStartDate: form.earlyBirdStartDate || '',
      earlyBirdEndDate: form.earlyBirdEndDate || '',
      regularPrice: form.regularPrice ?? 0,
      priceEndDate: form.priceEndDate || '',
      tokenGatingEnabled: form.tokenGatingEnabled ?? false,
      tokenSymbol: form.tokenSymbol || 'GTRA',
      tokenContractAddress: form.tokenContractAddress || '',
      tokenMinHolding: form.tokenMinHolding ?? 0,
      tokenHoldersOnly: form.tokenHoldersOnly ?? false,
    };

    try {
      if (editing && !isCopy) {
        const updated = await updateAdminEvent(editing.id, eventData);
        if (updated) {
          setEvents(prev => prev.map(ev => ev.id === editing.id ? updated : ev));
          showToast('活动信息已更新');
        } else {
          showToast('更新失败');
          return;
        }
      } else {
        const created = await createAdminEvent(eventData);
        if (created) {
          setEvents(prev => [...prev, created]);
          showToast('活动添加成功');
        } else {
          showToast('创建失败');
          return;
        }
      }
      setIsCopy(false);
      setShowModal(false);
    } catch (error) {
      console.error('Failed to save event:', error);
      showToast('保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = (ev: FrontendEvent) => {
    const copied: Partial<FrontendEvent> = {
      ...ev,
      title: `【副本】${ev.title}`,
      titleEn: `[Copy] ${ev.titleEn}`,
      status: 'upcoming',
      date: '',
      endDate: '',
      location: '',
      locationEn: '',
    };
    // Open edit modal immediately so admin can fill date/location
    openEdit(copied as FrontendEvent, true);
    showToast(`已复制「${ev.title}」，请补充日期和地点`);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteAdminEvent(id);
      setEvents(prev => prev.filter(ev => ev.id !== id));
      setDeleteConfirm(null);
      showToast('活动已删除');
    } catch (error) {
      console.error('Failed to delete event:', error);
      showToast('删除失败');
    }
  };

  const f = (key: keyof FrontendEvent, val: string | number | undefined) =>
    setForm((prev) => ({ ...prev, [key]: val }));

  const getRegistrationCount = (eventId: string): number => {
    try {
      const raw = localStorage.getItem('gtra_admin_registrations');
      if (!raw) return 0;
      const regs = JSON.parse(raw) as Array<{ eventId: string; status: string }>;
      return regs.filter((r) => r.eventId === eventId && r.status === 'approved').length;
    } catch {
      return 0;
    }
  };

  if (!canView) return <NoPermission resource="活动管理" />;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {toast && (
        <div className="fixed top-5 right-5 z-50 px-4 py-2.5 bg-[#18181B] text-white text-sm rounded-xl animate-fadeIn shadow-lg">
          {toast}
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#18181B]">活动管理</h1>
          <p className="text-sm text-[#6B7280] mt-0.5">共 {events.length} 场活动</p>
        </div>
        {canWrite && (
          <button onClick={openAdd} className="flex items-center gap-2 px-4 py-2.5 bg-[#18181B] text-white text-sm font-medium rounded-lg hover:bg-[#27272A] cursor-pointer whitespace-nowrap transition-colors">
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line" /></div>
            添加活动
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative">
          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none w-4 h-full">
            <i className="ri-search-line text-[#9CA3AF] text-sm" />
          </div>
          <input type="text" placeholder="搜索活动名称/地点..." value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-4 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none bg-white w-52" />
        </div>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg bg-white focus:outline-none cursor-pointer">
          <option value="all">全部状态</option>
          <option value="upcoming">即将举行</option>
          <option value="past">已结束</option>
        </select>
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)}
          className="px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg bg-white focus:outline-none cursor-pointer">
          <option value="all">全部类型</option>
          <option value="offline">线下</option>
          <option value="online">线上</option>
          <option value="hybrid">混合</option>
        </select>
        <span className="flex items-center text-xs text-[#9CA3AF] ml-auto">{filtered.length} 场活动</span>
      </div>

      {/* Batch Action Bar */}
      {someSelected && (
        <div className="flex items-center gap-3 px-4 py-2.5 mb-3 bg-[#18181B] rounded-xl">
          <span className="text-white text-xs font-medium whitespace-nowrap">已选 {selectedIds.size} 项</span>
          <div className="flex-1" />
          {canWrite && <>
            <button onClick={() => handleBatchStatus('upcoming')} className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded-lg cursor-pointer whitespace-nowrap transition-colors">
              <div className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-calendar-check-line" /></div>设为即将举行
            </button>
            <button onClick={() => handleBatchStatus('past')} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#374151] hover:bg-[#4B5563] text-white text-xs font-medium rounded-lg cursor-pointer whitespace-nowrap transition-colors">
              <div className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-history-line" /></div>设为已结束
            </button>
          </>}
          {canDelete && (
            <button onClick={() => setBatchDeleteConfirm(true)} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-xs font-medium rounded-lg cursor-pointer whitespace-nowrap transition-colors">
              <div className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-delete-bin-line" /></div>批量删除
            </button>
          )}
          <button onClick={() => setSelectedIds(new Set())} className="w-6 h-6 flex items-center justify-center text-white/50 hover:text-white cursor-pointer transition-colors">
            <i className="ri-close-line text-sm" />
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="bg-white rounded-xl border border-[#E5E7EB] p-12">
          <div className="flex flex-col items-center justify-center">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#18181B] border-t-transparent mb-4"></div>
            <p className="text-sm text-[#6B7280]">加载中...</p>
          </div>
        </div>
      )}

      {/* Events Table */}
      {!loading && (
        <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#F3F4F6] bg-[#FAFAFA]">
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={allFilteredSelected}
                      onChange={toggleSelectAll}
                      className="w-3.5 h-3.5 rounded border-[#D1D5DB] cursor-pointer accent-[#18181B]"
                    />
                  </th>
                  {['封面', '活动名称', '日期', '地点', '类型', '状态', '报名人数', '最高容量', '操作'].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-medium text-[#9CA3AF] whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={10} className="text-center py-12 text-[#9CA3AF] text-sm">暂无活动数据</td></tr>
                ) : filtered.map((ev) => {
                  const regCount = getRegistrationCount(ev.id);
                  const atCapacity = ev.maxCapacity ? regCount >= ev.maxCapacity : false;
                  const isSelected = selectedIds.has(ev.id);
                  return (
                    <tr key={ev.id} className={`border-b border-[#F9FAFB] transition-colors ${isSelected ? 'bg-amber-50/60' : 'hover:bg-[#FAFAFA]'}`}>
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelectOne(ev.id)}
                          className="w-3.5 h-3.5 rounded border-[#D1D5DB] cursor-pointer accent-[#18181B]"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="w-16 h-10 rounded-lg overflow-hidden bg-[#F3F4F6] shrink-0">
                          {ev.cover ? (
                            <img src={ev.cover} alt={ev.title} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <i className="ri-image-line text-[#D1D5DB]" />
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-[#18181B] text-xs max-w-[160px] truncate">{ev.title}</div>
                        <div className="text-[#9CA3AF] text-[10px] max-w-[160px] truncate">{ev.titleEn}</div>
                      </td>
                      <td className="px-4 py-3 text-[#6B7280] text-xs whitespace-nowrap">{ev.date} ~ {ev.endDate}</td>
                      <td className="px-4 py-3 text-[#6B7280] text-xs max-w-[100px] truncate">{ev.location}</td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${TYPE_STYLE[ev.type]}`}>
                          {TYPE_LABEL[ev.type]}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[ev.status]}`}>
                          {STATUS_LABEL[ev.status]}
                        </span>
                      </td>
                      <td className="px-4 py-3 min-w-[120px]">
                        {ev.maxCapacity ? (() => {
                          const pct = Math.min(100, Math.round((regCount / ev.maxCapacity) * 100));
                          const barColor = pct >= 100 ? '#EF4444' : pct >= 80 ? '#F59E0B' : '#10B981';
                          return (
                            <div>
                              <div className="flex items-center justify-between mb-1">
                                <span className={`text-xs font-semibold ${pct >= 100 ? 'text-red-500' : pct >= 80 ? 'text-amber-500' : 'text-[#18181B]'}`}>
                                  {regCount} / {ev.maxCapacity}
                                </span>
                                <span className={`text-[10px] font-medium ${pct >= 100 ? 'text-red-500' : pct >= 80 ? 'text-amber-500' : 'text-[#9CA3AF]'}`}>
                                  {pct}%
                                </span>
                              </div>
                              <div className="h-1.5 bg-[#F3F4F6] rounded-full overflow-hidden">
                                <div
                                  className="h-full rounded-full transition-all duration-500"
                                  style={{ width: `${pct}%`, backgroundColor: barColor }}
                                />
                              </div>
                              {pct >= 100 && (
                                <span className="text-[10px] text-red-500 font-medium mt-0.5 block">已满员</span>
                              )}
                            </div>
                          );
                        })() : (
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-semibold text-[#18181B]">{regCount}</span>
                            <span className="text-[10px] text-[#D1D5DB]">不限</span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-[#6B7280] text-xs">
                        {ev.maxCapacity ? (
                          <span className="font-medium text-[#18181B]">{ev.maxCapacity} 人</span>
                        ) : (
                          <span className="text-[#D1D5DB]">不限</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          {canWrite && (
                            <button onClick={() => openEdit(ev)} className="px-2 py-1 text-[10px] font-medium text-[#374151] bg-[#F3F4F6] hover:bg-[#E5E7EB] rounded cursor-pointer whitespace-nowrap transition-colors">编辑</button>
                          )}
                          {canWrite && (
                            <button onClick={() => handleCopy(ev)} title="复制活动" className="px-2 py-1 text-[10px] font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded cursor-pointer whitespace-nowrap transition-colors flex items-center gap-0.5">
                              <div className="w-3 h-3 flex items-center justify-center"><i className="ri-file-copy-line text-[10px]" /></div>复制
                            </button>
                          )}
                          {canDelete && (
                            <button onClick={() => setDeleteConfirm(ev.id)} className="px-2 py-1 text-[10px] font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded cursor-pointer whitespace-nowrap transition-colors">删除</button>
                          )}
                          {!canWrite && !canDelete && (
                            <span className="text-[10px] text-[#D1D5DB]">仅查看</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30" onClick={() => { setShowModal(false); setIsCopy(false); }}>
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className={`flex items-center justify-between px-6 py-4 border-b shrink-0 ${isCopy ? 'border-red-200 bg-red-50/40' : 'border-[#F3F4F6]'}`}>
              <div className="flex items-center gap-2">
                {isCopy && (
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-file-copy-line text-red-500 text-sm" />
                  </div>
                )}
                <h3 className={`font-bold ${isCopy ? 'text-red-700' : 'text-[#18181B]'}`}>
                  {isCopy ? '编辑活动副本 — 请更新日期和地点' : editing ? '编辑活动' : '添加活动'}
                </h3>
              </div>
              <button onClick={() => { setShowModal(false); setIsCopy(false); }} className="w-7 h-7 flex items-center justify-center text-[#9CA3AF] hover:text-[#6B7280] cursor-pointer">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            {/* Tab Navigation */}
            <div className="flex items-center gap-1 px-6 py-2 border-b border-[#F3F4F6] bg-[#FAFAFA]">
              <button
                onClick={() => setActiveTab('basic')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === 'basic'
                    ? 'bg-white text-[#18181B] shadow-sm'
                    : 'text-[#6B7280] hover:text-[#374151] hover:bg-white/50'
                }`}
              >
                基础信息
              </button>
              <button
                onClick={() => setActiveTab('gallery')}
                disabled={!editing}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-1.5 ${
                  activeTab === 'gallery'
                    ? 'bg-white text-[#18181B] shadow-sm'
                    : 'text-[#6B7280] hover:text-[#374151] hover:bg-white/50'
                } ${!editing ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <i className="ri-image-add-line text-sm" />
                相册
                {galleryArr.length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-amber-100 text-amber-700 rounded-full">
                    {galleryArr.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('agenda')}
                disabled={!editing}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-1.5 ${
                  activeTab === 'agenda'
                    ? 'bg-white text-[#18181B] shadow-sm'
                    : 'text-[#6B7280] hover:text-[#374151] hover:bg-white/50'
                } ${!editing ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <i className="ri-time-line text-sm" />
                议程
                {agendas.length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-amber-100 text-amber-700 rounded-full">
                    {agendas.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('guests')}
                disabled={!editing}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-1.5 ${
                  activeTab === 'guests'
                    ? 'bg-white text-[#18181B] shadow-sm'
                    : 'text-[#6B7280] hover:text-[#374151] hover:bg-white/50'
                } ${!editing ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <i className="ri-user-star-line text-sm" />
                嘉宾
                {guests.length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-amber-100 text-amber-700 rounded-full">
                    {guests.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('pricing')}
                disabled={!editing}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-1.5 ${
                  activeTab === 'pricing'
                    ? 'bg-white text-[#18181B] shadow-sm'
                    : 'text-[#6B7280] hover:text-[#374151] hover:bg-white/50'
                } ${!editing ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <i className="ri-money-dollar-circle-line text-sm" />
                价格
              </button>
            </div>
            
            {/* Tab Content */}
            <div className="overflow-y-auto flex-1">
              {activeTab === 'basic' && (
                <form onSubmit={handleSave} className="px-6 py-4 space-y-5">
                  {/* Copy hint banner */}
                  {isCopy && (
                    <div className="flex items-start gap-2.5 px-4 py-3 bg-red-50 border border-red-200 rounded-xl">
                      <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                        <i className="ri-error-warning-line text-red-500 text-sm" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-red-700">这是活动副本，请更新关键信息</p>
                        <p className="text-[10px] text-red-500 mt-0.5">红色边框标记的字段需要重新填写，避免与原活动信息重复</p>
                      </div>
                    </div>
                  )}
                  <ImageUploader
                    value={(form.cover as string) || ''}
                    onChange={(url) => f('cover', url)}
                    label="封面图片"
                    aspectClass="aspect-video"
                  />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1">活动名称（中）*</label>
                  <input type="text" value={(form.title as string) || ''} onChange={(e) => f('title', e.target.value)} required
                    className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B]" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1">活动名称（英）*</label>
                  <input type="text" value={(form.titleEn as string) || ''} onChange={(e) => f('titleEn', e.target.value)} required
                    className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B]" />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1 flex items-center gap-1">
                    <span className={isCopy ? 'text-red-600 font-semibold' : 'text-[#374151]'}>开始日期 *</span>
                    {isCopy && <span className="text-[10px] text-red-400">请更新</span>}
                  </label>
                  <input type="date" value={(form.date as string) || ''} onChange={(e) => f('date', e.target.value)} required
                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none transition-colors ${isCopy ? 'border-red-400 ring-2 ring-red-100 focus:border-red-500' : 'border-[#E5E7EB] focus:ring-2 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B]'}`} />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1 flex items-center gap-1">
                    <span className={isCopy ? 'text-red-600 font-semibold' : 'text-[#374151]'}>结束日期</span>
                    {isCopy && <span className="text-[10px] text-red-400">请更新</span>}
                  </label>
                  <input type="date" value={(form.endDate as string) || ''} onChange={(e) => f('endDate', e.target.value)}
                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none transition-colors ${isCopy ? 'border-red-400 ring-2 ring-red-100 focus:border-red-500' : 'border-[#E5E7EB] focus:ring-2 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B]'}`} />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1 flex items-center gap-1">
                    <span className={isCopy ? 'text-red-600 font-semibold' : 'text-[#374151]'}>地点（中）</span>
                    {isCopy && <span className="text-[10px] text-red-400">请更新</span>}
                  </label>
                  <input type="text" value={(form.location as string) || ''} onChange={(e) => f('location', e.target.value)}
                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none transition-colors ${isCopy ? 'border-red-400 ring-2 ring-red-100 focus:border-red-500' : 'border-[#E5E7EB] focus:ring-2 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B]'}`} />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1 flex items-center gap-1">
                    <span className={isCopy ? 'text-red-600 font-semibold' : 'text-[#374151]'}>地点（英）</span>
                    {isCopy && <span className="text-[10px] text-red-400">请更新</span>}
                  </label>
                  <input type="text" value={(form.locationEn as string) || ''} onChange={(e) => f('locationEn', e.target.value)}
                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none transition-colors ${isCopy ? 'border-red-400 ring-2 ring-red-100 focus:border-red-500' : 'border-[#E5E7EB] focus:ring-2 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B]'}`} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1">国家（中）</label>
                  <input type="text" value={(form.country as string) || ''} onChange={(e) => f('country', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B]" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1">地区</label>
                  <select value={(form.region as string) || 'asia'} onChange={(e) => f('region', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none cursor-pointer">
                    {REGION_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1">活动类型</label>
                  <select value={(form.type as string) || 'offline'} onChange={(e) => f('type', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none cursor-pointer">
                    <option value="offline">线下</option>
                    <option value="online">线上</option>
                    <option value="hybrid">混合</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1">状态</label>
                  <select value={(form.status as string) || 'upcoming'} onChange={(e) => f('status', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none cursor-pointer">
                    <option value="upcoming">即将举行</option>
                    <option value="past">已结束</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1">最高报名人数</label>
                  <div className="relative">
                    <input
                      type="number"
                      value={(form.maxCapacity as number) || ''}
                      onChange={(e) => f('maxCapacity', e.target.value ? Number(e.target.value) : undefined)}
                      min={1}
                      placeholder="不限"
                      className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B]"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] text-xs pointer-events-none">人</div>
                  </div>
                  <p className="text-[10px] text-[#9CA3AF] mt-1">留空则不限制报名人数</p>
                </div>

                {/* Registration window */}
                <div className="col-span-2">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-time-line text-[#F59E0B] text-xs" />
                    </div>
                    <span className="text-xs font-semibold text-[#374151]">报名开放时间窗口</span>
                    <span className="text-[10px] text-[#9CA3AF]">可选 — 限定用户可报名的起止日期</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 p-3 bg-amber-50/60 rounded-xl border border-amber-100">
                    <div>
                      <label className="block text-xs font-medium text-[#374151] mb-1">报名开始日期</label>
                      <input
                        type="date"
                        value={(form.registrationStart as string) || ''}
                        onChange={(e) => f('registrationStart', e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-[#E5E7EB] bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B]"
                      />
                      <p className="text-[10px] text-[#9CA3AF] mt-1">留空则即刻开放</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#374151] mb-1">报名截止日期</label>
                      <input
                        type="date"
                        value={(form.registrationDeadline as string) || ''}
                        onChange={(e) => f('registrationDeadline', e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-[#E5E7EB] bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B]"
                      />
                      <p className="text-[10px] text-[#9CA3AF] mt-1">留空则不限截止日期</p>
                    </div>
                  </div>
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-[#374151] mb-1">标签（中，逗号分隔）</label>
                  <input type="text" value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} placeholder="如：人工智能、量子计算"
                    className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B]" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-[#374151] mb-1">标签（英，逗号分隔）</label>
                  <input type="text" value={tagsEnInput} onChange={(e) => setTagsEnInput(e.target.value)} placeholder="e.g. AI, Quantum Computing"
                    className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B]" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-[#374151] mb-1">活动简介（中）</label>
                  <textarea value={(form.description as string) || ''} onChange={(e) => f('description', e.target.value)} rows={3}
                    className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B] resize-none" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-[#374151] mb-1">活动简介（英）</label>
                  <textarea value={(form.descriptionEn as string) || ''} onChange={(e) => f('descriptionEn', e.target.value)} rows={3}
                    className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B] resize-none" />
                </div>
              </div>
              <div className="flex gap-3 pt-3 border-t border-[#F3F4F6]">
                <button type="button" onClick={() => { setShowModal(false); setIsCopy(false); }}
                  className="flex-1 py-2.5 text-sm text-[#6B7280] bg-[#F3F4F6] hover:bg-[#E5E7EB] rounded-lg cursor-pointer whitespace-nowrap transition-colors"
                  disabled={saving}>
                  取消
                </button>
                <button type="submit"
                  className={`flex-1 py-2.5 text-sm font-medium text-white rounded-lg cursor-pointer whitespace-nowrap transition-colors flex items-center justify-center gap-2 ${isCopy ? 'bg-red-600 hover:bg-red-700' : 'bg-[#18181B] hover:bg-[#27272A]'}`}
                  disabled={saving}>
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      保存中...
                    </>
                  ) : (
                    isCopy ? '保存副本信息' : editing ? '保存修改' : '添加活动'
                  )}
                </button>
              </div>
                </form>
              )}
              
              {/* Gallery Tab */}
              {activeTab === 'gallery' && editing && (
                <div className="px-6 py-4">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="text-sm font-semibold text-[#18181B]">活动相册</h4>
                      <p className="text-[10px] text-[#9CA3AF] mt-0.5">管理历史活动的照片，用于前端展示轮播图</p>
                    </div>
                  </div>
                  
                  <div className="bg-[#FAFAFA] rounded-xl border border-[#E5E7EB] p-6">
                    <GalleryUploader
                      value={galleryArr}
                      onChange={setGalleryArr}
                      label="活动照片"
                      maxImages={20}
                    />
                  </div>
                  
                  {/* Preview Section */}
                  {galleryArr.length > 0 && (
                    <div className="mt-6">
                      <div className="flex items-center justify-between mb-3">
                        <h5 className="text-xs font-semibold text-[#374151]">预览效果</h5>
                        <span className="text-[10px] text-[#9CA3AF]">这是在前端页面中的展示样式</span>
                      </div>
                      <div className="grid grid-cols-6 gap-2">
                        {galleryArr.map((url, index) => (
                          <div key={`preview-${index}`} className="aspect-square rounded-lg overflow-hidden border border-[#E5E7EB]">
                            <img src={url} alt={`预览 ${index + 1}`} className="w-full h-full object-cover" />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Agenda Tab */}
              {activeTab === 'agenda' && editing && (
                <div className="px-6 py-4">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="text-sm font-semibold text-[#18181B]">活动议程</h4>
                      <p className="text-[10px] text-[#9CA3AF] mt-0.5">管理活动的日程安排和演讲信息</p>
                    </div>
                    <button
                      onClick={() => openAddAgenda(parseInt(editing.id))}
                      className="flex items-center gap-1.5 px-3 py-2 bg-[#18181B] text-white text-xs font-medium rounded-lg hover:bg-[#27272A] cursor-pointer transition-colors"
                    >
                      <i className="ri-add-line" />
                      添加议程
                    </button>
                  </div>
                  
                  {agendaLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#18181B] border-t-transparent"></div>
                    </div>
                  ) : agendas.length === 0 ? (
                    <div className="text-center py-12 bg-[#FAFAFA] rounded-xl border border-[#E5E7EB]">
                      <div className="w-12 h-12 rounded-full bg-[#F3F4F6] flex items-center justify-center mx-auto mb-3">
                        <i className="ri-time-line text-[#9CA3AF] text-xl" />
                      </div>
                      <p className="text-sm text-[#6B7280]">暂无议程</p>
                      <p className="text-[10px] text-[#9CA3AF] mt-1">点击上方按钮添加活动议程</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {agendas
                        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
                        .map((agenda, index) => (
                          <div key={agenda.id} className="bg-[#FAFAFA] rounded-xl border border-[#E5E7EB] p-4 hover:border-[#D1D5DB] transition-colors">
                            <div className="flex items-start gap-3">
                              <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold shrink-0">
                                {index + 1}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-xs font-semibold text-[#18181B]">{agenda.time}</span>
                                  {agenda.end_time && (
                                    <span className="text-[10px] text-[#9CA3AF]">~ {agenda.end_time}</span>
                                  )}
                                </div>
                                <p className="text-sm font-medium text-[#18181B] truncate">{agenda.title}</p>
                                {agenda.title_en && (
                                  <p className="text-[10px] text-[#9CA3AF] truncate">{agenda.title_en}</p>
                                )}
                                {agenda.speaker_name && (
                                  <div className="flex items-center gap-1.5 mt-2">
                                    <i className="ri-user-line text-[#9CA3AF] text-xs" />
                                    <span className="text-xs text-[#6B7280]">{agenda.speaker_name}</span>
                                    {agenda.speaker_title && (
                                      <span className="text-[10px] text-[#9CA3AF]">· {agenda.speaker_title}</span>
                                    )}
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                <button
                                  onClick={() => openEditAgenda(agenda)}
                                  className="px-2 py-1 text-[10px] font-medium text-[#374151] bg-white hover:bg-[#F3F4F6] border border-[#E5E7EB] rounded cursor-pointer transition-colors"
                                >
                                  编辑
                                </button>
                                <button
                                  onClick={() => {
                                    if (agenda.id && confirm('确认删除此议程？')) {
                                      handleDeleteAgenda(agenda.id);
                                    }
                                  }}
                                  className="px-2 py-1 text-[10px] font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded cursor-pointer transition-colors"
                                >
                                  删除
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* Guests Tab */}
              {activeTab === 'guests' && editing && (
                <div className="px-6 py-4">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="text-sm font-semibold text-[#18181B]">活动嘉宾</h4>
                      <p className="text-[10px] text-[#9CA3AF] mt-0.5">管理活动参与嘉宾和演讲主题</p>
                    </div>
                    <button
                      onClick={() => openAddGuest(parseInt(editing.id))}
                      className="flex items-center gap-1.5 px-3 py-2 bg-[#18181B] text-white text-xs font-medium rounded-lg hover:bg-[#27272A] cursor-pointer transition-colors"
                    >
                      <i className="ri-add-line" />
                      添加嘉宾
                    </button>
                  </div>
                  
                  {guestLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#18181B] border-t-transparent"></div>
                    </div>
                  ) : guests.length === 0 ? (
                    <div className="text-center py-12 bg-[#FAFAFA] rounded-xl border border-[#E5E7EB]">
                      <div className="w-12 h-12 rounded-full bg-[#F3F4F6] flex items-center justify-center mx-auto mb-3">
                        <i className="ri-user-star-line text-[#9CA3AF] text-xl" />
                      </div>
                      <p className="text-sm text-[#6B7280]">暂无嘉宾</p>
                      <p className="text-[10px] text-[#9CA3AF] mt-1">点击上方按钮添加活动嘉宾</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {guests
                        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
                        .map((guest) => (
                          <div key={guest.id} className="bg-[#FAFAFA] rounded-xl border border-[#E5E7EB] p-4 hover:border-[#D1D5DB] transition-colors">
                            <div className="flex items-start gap-3">
                              <div className="w-12 h-12 rounded-full bg-[#18181B] text-white flex items-center justify-center text-sm font-bold shrink-0 overflow-hidden">
                                {guest.guest?.avatar ? (
                                  <img src={guest.guest.avatar} alt={guest.guest.name} className="w-full h-full object-cover" />
                                ) : (
                                  guest.guest?.name?.charAt(0) || 'G'
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-sm font-semibold text-[#18181B]">{guest.guest?.name || '未知嘉宾'}</span>
                                  {guest.role && (
                                    <span className="text-[10px] px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full">{guest.role}</span>
                                  )}
                                </div>
                                {guest.guest?.title && (
                                  <p className="text-xs text-[#6B7280]">{guest.guest.title}</p>
                                )}
                                {guest.guest?.institution && (
                                  <p className="text-[10px] text-[#9CA3AF]">{guest.guest.institution}</p>
                                )}
                                {guest.topic && (
                                  <div className="mt-2 pt-2 border-t border-[#E5E7EB]">
                                    <p className="text-xs text-[#374151]">
                                      <span className="font-medium">演讲主题：</span>{guest.topic}
                                    </p>
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                <button
                                  onClick={() => openEditGuest(guest)}
                                  className="px-2 py-1 text-[10px] font-medium text-[#374151] bg-white hover:bg-[#F3F4F6] border border-[#E5E7EB] rounded cursor-pointer transition-colors"
                                >
                                  编辑
                                </button>
                                <button
                                  onClick={() => {
                                    if (guest.id && confirm('确认移除此嘉宾？')) {
                                      handleDeleteGuest(guest.id);
                                    }
                                  }}
                                  className="px-2 py-1 text-[10px] font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded cursor-pointer transition-colors"
                                >
                                  移除
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* Pricing Tab */}
              {activeTab === 'pricing' && editing && (
                <div className="px-6 py-4">
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-[#18181B]">活动价格设置</h4>
                    <p className="text-[10px] text-[#9CA3AF] mt-0.5">配置活动的门票价格、早鸟价和Token免费票</p>
                  </div>
                  <PricingSettings
                    eventId={parseInt(editing.id)}
                    initialData={{
                      is_paid_event: form.isPaidEvent || false,
                      currency: form.currency || 'USD',
                      early_bird_enabled: form.earlyBirdEnabled || false,
                      early_bird_price: form.earlyBirdPrice || 0,
                      early_bird_start_date: form.earlyBirdStartDate || '',
                      early_bird_end_date: form.earlyBirdEndDate || '',
                      regular_price: form.regularPrice || 0,
                      price_end_date: form.priceEndDate || '',
                      token_gating_enabled: form.tokenGatingEnabled || false,
                      token_symbol: form.tokenSymbol || 'GTRA',
                      token_contract_address: form.tokenContractAddress || '',
                      token_min_holding: form.tokenMinHolding || 0,
                      token_holders_only: form.tokenHoldersOnly || false,
                    }}
                    onSave={async (data) => {
                      // Update form with pricing data
                      setForm(prev => ({
                        ...prev,
                        isPaidEvent: data.is_paid_event,
                        currency: data.currency,
                        earlyBirdEnabled: data.early_bird_enabled,
                        earlyBirdPrice: data.early_bird_price,
                        earlyBirdStartDate: data.early_bird_start_date,
                        earlyBirdEndDate: data.early_bird_end_date,
                        regularPrice: data.regular_price,
                        priceEndDate: data.price_end_date,
                        tokenGatingEnabled: data.token_gating_enabled,
                        tokenSymbol: data.token_symbol,
                        tokenContractAddress: data.token_contract_address,
                        tokenMinHolding: data.token_min_holding,
                        tokenHoldersOnly: data.token_holders_only,
                      }));
                      // Save to backend
                      if (editing) {
                        await updateAdminEvent(editing.id, {
                          isPaidEvent: data.is_paid_event,
                          currency: data.currency,
                          earlyBirdEnabled: data.early_bird_enabled,
                          earlyBirdPrice: data.early_bird_price,
                          earlyBirdStartDate: data.early_bird_start_date,
                          earlyBirdEndDate: data.early_bird_end_date,
                          regularPrice: data.regular_price,
                          priceEndDate: data.price_end_date,
                          tokenGatingEnabled: data.token_gating_enabled,
                          tokenSymbol: data.token_symbol,
                          tokenContractAddress: data.token_contract_address,
                          tokenMinHolding: data.token_min_holding,
                          tokenHoldersOnly: data.token_holders_only,
                        });
                        showToast('价格设置已保存');
                      }
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Single Delete Confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
            <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center mb-4 mx-auto">
              <i className="ri-delete-bin-line text-red-500 text-lg" />
            </div>
            <h3 className="font-bold text-[#18181B] text-center mb-2">确认删除</h3>
            <p className="text-sm text-[#6B7280] text-center mb-5">删除后无法恢复，确认要删除这场活动吗？</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)}
                className="flex-1 py-2.5 text-sm text-[#6B7280] bg-[#F3F4F6] rounded-lg cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 py-2.5 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg cursor-pointer whitespace-nowrap">确认删除</button>
            </div>
          </div>
        </div>
      )}

      {/* Batch Delete Confirm */}
      {batchDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
            <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center mb-4 mx-auto">
              <i className="ri-delete-bin-line text-red-500 text-lg" />
            </div>
            <h3 className="font-bold text-[#18181B] text-center mb-2">批量删除确认</h3>
            <p className="text-sm text-[#6B7280] text-center mb-5">
              即将删除 <span className="font-semibold text-red-500">{selectedIds.size}</span> 场活动，此操作不可撤销，确认继续？
            </p>
            <div className="flex gap-3">
              <button onClick={() => setBatchDeleteConfirm(false)}
                className="flex-1 py-2.5 text-sm text-[#6B7280] bg-[#F3F4F6] rounded-lg cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={handleBatchDelete}
                className="flex-1 py-2.5 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg cursor-pointer whitespace-nowrap">确认删除</button>
            </div>
          </div>
        </div>
      )}

      {/* Agenda Edit Modal */}
      {showAgendaModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/30" onClick={() => setShowAgendaModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#F3F4F6]">
              <h3 className="font-bold text-[#18181B]">
                {editingAgenda?.id ? '编辑议程' : '添加议程'}
              </h3>
              <button onClick={() => setShowAgendaModal(false)} className="w-7 h-7 flex items-center justify-center text-[#9CA3AF] hover:text-[#6B7280] cursor-pointer">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <form onSubmit={handleSaveAgenda} className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1">开始时间 *</label>
                  <input
                    type="time"
                    value={agendaForm.time || ''}
                    onChange={(e) => setAgendaForm(prev => ({ ...prev, time: e.target.value }))}
                    required
                    className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1">结束时间</label>
                  <input
                    type="time"
                    value={agendaForm.end_time || ''}
                    onChange={(e) => setAgendaForm(prev => ({ ...prev, end_time: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B]"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#374151] mb-1">议程标题（中）*</label>
                <input
                  type="text"
                  value={agendaForm.title || ''}
                  onChange={(e) => setAgendaForm(prev => ({ ...prev, title: e.target.value }))}
                  required
                  placeholder="如：开幕致辞"
                  className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B]"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#374151] mb-1">议程标题（英）</label>
                <input
                  type="text"
                  value={agendaForm.title_en || ''}
                  onChange={(e) => setAgendaForm(prev => ({ ...prev, title_en: e.target.value }))}
                  placeholder="e.g. Opening Remarks"
                  className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B]"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1">地点（中）</label>
                  <input
                    type="text"
                    value={agendaForm.location || ''}
                    onChange={(e) => setAgendaForm(prev => ({ ...prev, location: e.target.value }))}
                    placeholder="如：主会场"
                    className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1">地点（英）</label>
                  <input
                    type="text"
                    value={agendaForm.location_en || ''}
                    onChange={(e) => setAgendaForm(prev => ({ ...prev, location_en: e.target.value }))}
                    placeholder="e.g. Main Hall"
                    className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B]"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#374151] mb-1">演讲嘉宾姓名</label>
                <input
                  type="text"
                  value={agendaForm.speaker_name || ''}
                  onChange={(e) => setAgendaForm(prev => ({ ...prev, speaker_name: e.target.value }))}
                  placeholder="如：张三"
                  className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B]"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1">嘉宾职位/头衔</label>
                  <input
                    type="text"
                    value={agendaForm.speaker_title || ''}
                    onChange={(e) => setAgendaForm(prev => ({ ...prev, speaker_title: e.target.value }))}
                    placeholder="如：教授、CEO"
                    className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1">嘉宾机构</label>
                  <input
                    type="text"
                    value={agendaForm.speaker_institution || ''}
                    onChange={(e) => setAgendaForm(prev => ({ ...prev, speaker_institution: e.target.value }))}
                    placeholder="如：清华大学"
                    className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B]"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#374151] mb-1">议程简介（中）</label>
                <textarea
                  value={agendaForm.description || ''}
                  onChange={(e) => setAgendaForm(prev => ({ ...prev, description: e.target.value }))}
                  rows={2}
                  placeholder="可选的议程详细说明"
                  className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B] resize-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#374151] mb-1">议程简介（英）</label>
                <textarea
                  value={agendaForm.description_en || ''}
                  onChange={(e) => setAgendaForm(prev => ({ ...prev, description_en: e.target.value }))}
                  rows={2}
                  placeholder="Optional description"
                  className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B] resize-none"
                />
              </div>
              <div className="flex gap-3 pt-3 border-t border-[#F3F4F6]">
                <button type="button" onClick={() => setShowAgendaModal(false)}
                  className="flex-1 py-2.5 text-sm text-[#6B7280] bg-[#F3F4F6] hover:bg-[#E5E7EB] rounded-lg cursor-pointer whitespace-nowrap transition-colors"
                  disabled={saving}>
                  取消
                </button>
                <button type="submit"
                  className="flex-1 py-2.5 text-sm font-medium text-white bg-[#18181B] hover:bg-[#27272A] rounded-lg cursor-pointer whitespace-nowrap transition-colors flex items-center justify-center gap-2"
                  disabled={saving}>
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      保存中...
                    </>
                  ) : editingAgenda?.id ? '保存修改' : '添加议程'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Guest Edit Modal */}
      {showGuestModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/30" onClick={() => setShowGuestModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#F3F4F6]">
              <h3 className="font-bold text-[#18181B]">
                {editingGuest?.id ? '编辑嘉宾' : '添加嘉宾'}
              </h3>
              <button onClick={() => setShowGuestModal(false)} className="w-7 h-7 flex items-center justify-center text-[#9CA3AF] hover:text-[#6B7280] cursor-pointer">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <form onSubmit={handleSaveGuest} className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-[#374151] mb-1">选择嘉宾 *</label>
                <select
                  value={guestForm.guest_id || 0}
                  onChange={(e) => {
                    const guestId = parseInt(e.target.value);
                    const selected = allGuests.find(g => g.id === guestId);
                    setGuestForm(prev => ({
                      ...prev,
                      guest_id: guestId,
                      guest: selected,
                    }));
                  }}
                  required
                  className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B] cursor-pointer"
                >
                  <option value={0}>请选择嘉宾</option>
                  {allGuests.map(guest => (
                    <option key={guest.id} value={guest.id}>
                      {guest.name} {guest.title ? `- ${guest.title}` : ''} {guest.institution ? `@ ${guest.institution}` : ''}
                    </option>
                  ))}
                </select>
                <p className="text-[10px] text-[#9CA3AF] mt-1">
                  如需添加新嘉宾，请先到嘉宾管理页面创建
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1">活动角色（中）</label>
                  <input
                    type="text"
                    value={guestForm.role || ''}
                    onChange={(e) => setGuestForm(prev => ({ ...prev, role: e.target.value }))}
                    placeholder="如：主旨演讲嘉宾"
                    className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1">活动角色（英）</label>
                  <input
                    type="text"
                    value={guestForm.role_en || ''}
                    onChange={(e) => setGuestForm(prev => ({ ...prev, role_en: e.target.value }))}
                    placeholder="e.g. Keynote Speaker"
                    className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B]"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#374151] mb-1">演讲主题（中）</label>
                <input
                  type="text"
                  value={guestForm.topic || ''}
                  onChange={(e) => setGuestForm(prev => ({ ...prev, topic: e.target.value }))}
                  placeholder="如：人工智能赋能产业升级"
                  className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B]"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#374151] mb-1">演讲主题（英）</label>
                <input
                  type="text"
                  value={guestForm.topic_en || ''}
                  onChange={(e) => setGuestForm(prev => ({ ...prev, topic_en: e.target.value }))}
                  placeholder="e.g. AI Empowering Industry Upgrade"
                  className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B]"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#374151] mb-1">排序权重</label>
                <input
                  type="number"
                  value={guestForm.sort_order || 0}
                  onChange={(e) => setGuestForm(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                  min={0}
                  className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B]"
                />
                <p className="text-[10px] text-[#9CA3AF] mt-1">数字越小排位越靠前</p>
              </div>
              <div className="flex gap-3 pt-3 border-t border-[#F3F4F6]">
                <button type="button" onClick={() => setShowGuestModal(false)}
                  className="flex-1 py-2.5 text-sm text-[#6B7280] bg-[#F3F4F6] hover:bg-[#E5E7EB] rounded-lg cursor-pointer whitespace-nowrap transition-colors"
                  disabled={saving}>
                  取消
                </button>
                <button type="submit"
                  className="flex-1 py-2.5 text-sm font-medium text-white bg-[#18181B] hover:bg-[#27272A] rounded-lg cursor-pointer whitespace-nowrap transition-colors flex items-center justify-center gap-2"
                  disabled={saving}>
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      保存中...
                    </>
                  ) : editingGuest?.id ? '保存修改' : '添加嘉宾'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
